/**
 * CreateLetter Dialog (Matter-centric)
 * ------------------------------------
 * Original dialog for creating a document from a **Matter** context.
 *
 *  • Expects `matter_id`, `template`, `firm_id` props.
 *  • Fetches merge-field definitions with `matterId` param so the backend can
 *    pre-populate defaults.
 *  • Uses shared `LetterFormContent` to render the inputs.
 *  • On submit calls `createDocument` mutation and **does not** send the
 *    document; that is handled in `PreviewLetter` once the user presses Send.
 *
 * Special case: for Letter of Representation templates it delegates to the
 * `CreateLoRDialog` variant.
 *
 * Intake flow does **not** use this component; it uses `SendLoeDialog` which
 * wraps similar UI but different backend flow.
 */
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  styled,
  Typography,
} from '@mui/material';

import type { ReadTemplateField } from '../../services/api/generated/templatesV2Service';
import {
  useTemplateFieldsQuery,
} from '../../services/api/generated/templatesV2Service';
import {
  useCreateDocumentMutation,
} from '../../services/api/lettersService';
import { DetailedMatter } from '../../services/types/client-intake-types';
import type { TemplateCategory } from '../../services/types/letters-types';
import {
  adaptTemplateFields,
  DocumentTemplate,
  DocumentTemplateFields,
} from '../../services/types/letters-types';
import { useNotification } from '../contexts/NotificationContext';
import { CreateLoRDialog } from './create-lor/CreateLoRDialog';
import { LetterFormContent } from './LetterFormShared';

const REQUIRED_CONTRACT_FIELDS = {
  CLIENT_NAME: {
    mapping: 'client_name',
    display_name: 'Client Name',
  },
  CLIENT_EMAIL: {
    mapping: 'client_email_address',
    display_name: 'Client Email',
  },
} as const;

const OPTIONAL_CONTRACT_FIELDS = {
  CLIENT_PHONE: {
    mapping: 'client_phone_number',
    display_name: 'Client Phone Number',
  },
} as const;

type RequiredField =
  (typeof REQUIRED_CONTRACT_FIELDS)[keyof typeof REQUIRED_CONTRACT_FIELDS]['mapping'];

type OptionalField =
  (typeof OPTIONAL_CONTRACT_FIELDS)[keyof typeof OPTIONAL_CONTRACT_FIELDS]['mapping'];

const StyledDialogTitle = styled(DialogTitle)(() => ({
  fontSize: 36,
  padding: 0,
}));

const StyledDialogContent = styled(DialogContent)(() => ({
  padding: 0,
}));

const StyledDialogActions = styled(DialogActions)(() => ({
  padding: 0,
  '& .MuiButton-contained': {
    width: '100%',
  },
}));

interface CreateLetterProps {
  open: boolean;
  onClose: () => void;
  matter_id: string;
  matter?: DetailedMatter;
  template: DocumentTemplate;
  firm_id: string;
}

interface LetterFormData {
  [key: string]: string;
}

export const CreateLetter = ({
  open,
  onClose,
  matter_id,
  matter,
  template,
  firm_id,
}: CreateLetterProps) => {
  const { data: rawTemplateFields, isLoading: isLoadingFields } =
    useTemplateFieldsQuery({ id: template.id });
  const templateFields: DocumentTemplateFields | undefined = rawTemplateFields
    ? {
        fields: adaptTemplateFields(
          rawTemplateFields as unknown as ReadTemplateField[],
        ),
      }
    : undefined;

  const notification = useNotification();
  const [createDocument, { isLoading: isCreating }] =
    useCreateDocumentMutation();

  // Guard against EXTERNAL (Adobe Sign) templates that cannot be rendered locally
  if (template.format === 'EXTERNAL') {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { padding: 4, borderRadius: 4 } }}
      >
        <Stack spacing={3}>
          <DialogTitle sx={{ padding: 0 }}>
            <Typography variant="h6">External Template</Typography>
          </DialogTitle>
          <DialogContent sx={{ padding: 0 }}>
            <Typography>
              This template is managed through Adobe Sign and cannot be created
              here. Please use Adobe Sign to send this document.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ padding: 0 }}>
            <Button variant="contained" onClick={onClose}>
              Close
            </Button>
          </DialogActions>
        </Stack>
      </Dialog>
    );
  }

  // Use specialized LoR dialog for Letter of Representation templates
  const LOR_CATEGORIES: TemplateCategory[] = [
    'LOR_GENERIC',
    'LOR_FIRST_PARTY',
    'LOR_THIRD_PARTY',
    'LOR_HEALTH',
  ];
  const isLoRTemplate = LOR_CATEGORIES.includes(template.category);

  // Render specialized LoR dialog for Letter of Representation templates
  if (isLoRTemplate) {
    return (
      <CreateLoRDialog
        isOpen={open}
        onClose={onClose}
        matterId={matter_id}
        template={template}
        firmId={firm_id}
      />
    );
  }

  const requiredMappings = template.requires_signatures
    ? ['client_name', 'client_email_address']
    : [];

  // Extract client information from matter for initial values
  const initialValues: Record<string, string> = {};
  if (matter?.clients && matter.clients.length > 0) {
    const primaryClient = matter.clients[0];
    if (primaryClient.name) {
      initialValues.client_name = primaryClient.name;
    }
    if (primaryClient.email) {
      initialValues.client_email_address = primaryClient.email;
    }
    // Also add phone number if available
    if (primaryClient.phone_numbers && primaryClient.phone_numbers.length > 0) {
      initialValues.client_phone_number =
        primaryClient.phone_numbers[0].phone_number;
    }
  }

  const handleSubmit = async (data: LetterFormData) => {
    try {
      // For contracts requiring signatures, ensure we always include client email and name
      const finalMergeFields = { ...data };

      if (template.requires_signatures) {
        // Check if we have client information
        if (!matter?.clients || matter.clients.length === 0) {
          notification(
            'Cannot create contract: No client information found in matter',
            'error',
            5000,
          );
          return;
        }

        const primaryClient = matter.clients[0];

        // Check for required email
        if (!primaryClient.email) {
          notification(
            'Cannot create contract: Client email address is required for signatures. Please add client email to the matter first.',
            'error',
            5000,
          );
          return;
        }

        // Always include these fields for contracts
        finalMergeFields.client_email_address = primaryClient.email;

        if (primaryClient.name) {
          finalMergeFields.client_name = primaryClient.name;
        }

        // Also include phone if available (optional for signatures)
        if (
          primaryClient.phone_numbers &&
          primaryClient.phone_numbers.length > 0
        ) {
          finalMergeFields.client_phone_number =
            primaryClient.phone_numbers[0].phone_number;
        }
      }

      await createDocument({
        matterId: matter_id,
        data: {
          document_template_id: template.id,
          merge_fields: finalMergeFields,
        },
      }).unwrap();
      onClose();
    } catch (error) {
      console.error('Failed to create document:', error);
      notification('Failed to create document', 'error', 5000);
    }
  };

  const title = template.requires_signatures
    ? 'Create Contract'
    : 'Create Letter';

  const actionText = template.requires_signatures
    ? 'Create Contract'
    : 'Create Letter';

  const isLoading = isLoadingFields || isCreating;

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          minWidth: 720,
          padding: 6,
          borderRadius: 4,
        },
      }}
    >
      <Stack spacing={6}>
        <StyledDialogTitle>
          <Typography variant="h1">{title}</Typography>
          <Typography variant="subtitle1">{template.name}</Typography>
        </StyledDialogTitle>
        <StyledDialogContent>
          <LetterFormContent
            isLoading={isLoadingFields}
            templateFields={templateFields}
            onSubmit={handleSubmit}
            requiredMappings={requiredMappings}
            initialValues={initialValues}
          />
        </StyledDialogContent>
        <StyledDialogActions>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            form="letter-form"
            disabled={isLoading}
            sx={{ height: 48 }}
          >
            <Typography variant="h3" sx={{ textTransform: 'none' }}>
              {isCreating ? 'Creating...' : actionText}
            </Typography>
          </Button>
        </StyledDialogActions>
      </Stack>
    </Dialog>
  );
};
